'use strict';

/**
 * Ticket #291 - Contact Form (Privacy Checkbox).
 *
 * Validation itself is intentionally left server-side (TYPO3 Form
 * Framework's own validators, e.g. the custom NotEmptyValidator on the
 * privacy checkbox) - this only fixes the UX around a resulting error.
 *
 * Problem: a form can sit inside a panel that starts hidden. On the
 * ticket's own page (Grosskunden "Kontaktformular" CTA) that's
 * ContentElements/Cta.html's inline toggle script, which gives the form
 * class "hidden-form" (opacity:0, height:0, visibility:hidden - see
 * CtaContent.scss) until its button swaps in "visible-form". The same
 * pattern exists wherever a form sits inside Molecule/AccordionItem's
 * .accordion__item--content instead (display:none until '.open').
 *
 * When a required field fails server-side validation, TYPO3 reloads the
 * page and correctly renders the error (Field/Field.html's
 * <div class="error form-text" role="alert">...</div> inside a
 * <div class="form-group has-error">), but the panel wrapping it is
 * collapsed again on the fresh page load - nothing re-opens it, so the
 * error is invisible until the visitor reopens the panel by hand.
 *
 * Fix: on page load, find any such server-rendered error, reveal
 * whichever hidden-panel pattern is wrapping it, and scroll to/focus the
 * associated field.
 */

const SERVER_ERROR_SELECTOR = '.error.form-text[role="alert"]';
const ACCORDION_CONTENT_SELECTOR = '[data-nezzo-accordion-toggle-item]';
const ACCORDION_ITEM_SELECTOR = '.accordion__item';
const ACCORDION_TOGGLE_SELECTOR = '[data-nezzo-accordion-toggle]';

// Same reveal logic for both hidden-panel patterns used on the site (see
// the header comment above for what each one is/where it's used).
const revealHiddenAncestor = (el) => {
    const accordionContent = el.closest(ACCORDION_CONTENT_SELECTOR);
    if (accordionContent) {
        accordionContent.classList.add('open');
        const item = accordionContent.closest(ACCORDION_ITEM_SELECTOR);
        const toggle = item && item.querySelector(ACCORDION_TOGGLE_SELECTOR);
        if (toggle) {
            toggle.classList.add('open');
        }
    }

    const hiddenForm = el.closest('.hidden-form');
    if (hiddenForm) {
        hiddenForm.classList.remove('hidden-form');
        hiddenForm.classList.add('visible-form');
    }
};

document.addEventListener('DOMContentLoaded', () => {
    // Deferred a macrotask (setTimeout 0) rather than run inline here: this
    // is one of possibly several DOMContentLoaded listeners on the page,
    // and ContentElements/Cta.html's own inline script - which
    // unconditionally (re-)adds "hidden-form" to its form on every load,
    // regardless of prior state - is another. Listeners for the same event
    // fire in registration order, which depends on where each script tag
    // sits in the page and whether it's deferred, so which one "wins" isn't
    // reliable. Queuing this as a new macrotask guarantees it runs only
    // after every DOMContentLoaded listener (including Cta's) has already
    // finished, so we're always reacting to the final hidden/visible state
    // instead of racing it.
    window.setTimeout(() => {
        const errors = Array.prototype.slice.call(document.querySelectorAll(SERVER_ERROR_SELECTOR));
        if (!errors.length) {
            return;
        }

        errors.forEach(revealHiddenAncestor);

        const formGroup = errors[0].closest('.form-group') || errors[0].parentElement;
        // Checkbox fields render a hidden companion input right before the
        // visible one (TYPO3's usual "always submit a value" pattern), so it
        // has to be excluded here - otherwise it matches first and its
        // getBoundingClientRect() is always {top: 0}, since hidden inputs
        // have no box, which sends the scroll target to the top of the page.
        const field = formGroup && formGroup.querySelector('input:not([type="hidden"]), select, textarea');

        // The Cta component's reveal animation is a 0.5s CSS transition
        // (see CtaContent.scss); wait for it so the field's final position
        // is used, not its still-collapsed one. The accordion component
        // reveals instantly, so this delay is just a harmless no-op there.
        window.setTimeout(() => {
            const target = field || errors[0];
            const top = target.getBoundingClientRect().top + window.pageYOffset - 136;
            // Plain instant scroll rather than {behavior: 'smooth'}: smooth
            // scrolling is compositor-driven and Chrome can silently skip
            // the animation (scroll position never moves) when the tab
            // isn't the focused/foregrounded one, which is exactly the
            // state a page often reloads into. An instant jump always
            // lands regardless of tab focus.
            window.scrollTo(0, top);
            if (field) {
                field.focus({preventScroll: true});
            }
        }, 550);
    }, 0);
});
